import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { MessageSquare, Search, Circle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import useWebSocket from '../hooks/useWebSocket';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const MessagesPageNew = () => {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // WebSocket for real-time updates
  const { lastMessage, isConnected } = useWebSocket(user?.id);

  useEffect(() => {
    // Wait for auth to fully load before making any decisions
    if (authLoading) {
      return;
    }
    
    // Don't fetch if not authenticated (redirect handled in render)
    if (!isAuthenticated) {
      return;
    }
    
    // User is authenticated, fetch conversations
    fetchConversations();
    
    // Poll for new messages every 30 seconds as backup
    const interval = setInterval(fetchConversations, 30000);
    return () => clearInterval(interval);
  }, [authLoading, isAuthenticated]); // Re-run when auth state changes
  
  // Listen for WebSocket messages and update conversations in real-time
  useEffect(() => {
    if (lastMessage) {
      console.log('Received WebSocket message:', lastMessage);
      
      // Refresh conversations when new message arrives
      if (lastMessage.type === 'message' || lastMessage.type === 'new_message') {
        fetchConversations();
      }
    }
  }, [lastMessage]);

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setConversations(response.data || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Parse the date string - handle both ISO format and MongoDB ISODate
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return '';
      }
      
      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);
      
      // Handle future dates (clock skew)
      if (diff < 0) return 'Just now';
      
      if (minutes < 1) return 'Just now';
      if (minutes < 60) return `${minutes}m ago`;
      if (hours < 24) return `${hours}h ago`;
      if (days === 1) return 'Yesterday';
      if (days < 7) return `${days}d ago`;
      
      // Use user's local timezone for dates older than 7 days
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting time:', error, dateString);
      return '';
    }
  };

  const filteredConversations = conversations.filter((conv) => {
    if (!searchTerm) return true;
    const name = `${conv.otherUser.firstName} ${conv.otherUser.lastName}`.toLowerCase();
    return name.includes(searchTerm.toLowerCase());
  });

  // ========================================
  // ALL REDIRECT LOGIC IS IN RENDER (declarative)
  // ========================================

  // 1️⃣ Wait for auth to load
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="w-full px-2">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // 2️⃣ Not authenticated → redirect to login (declarative)
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 3️⃣ Show loading while fetching conversations
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="w-full px-2">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading messages...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full px-2">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Messages</h1>
          <p className="text-gray-600">Chat with your matches</p>
        </div>

        {/* Search Bar */}
        {conversations.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search conversations..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Conversations List */}
        {filteredConversations.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm ? 'No conversations found' : 'No messages yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm
                ? 'Try searching with a different name'
                : 'Start a conversation by accepting interests from others or browsing profiles'}
            </p>
            {!searchTerm && (
              <div className="flex gap-4 justify-center">
                <Link
                  to="/interests"
                  className="bg-mint-500 text-white px-6 py-3 rounded-lg hover:bg-mint-600 transition-colors"
                >
                  View Interests
                </Link>
                <Link
                  to="/search"
                  className="bg-white border-2 border-mint-500 text-navy-500 px-6 py-3 rounded-lg hover:bg-cream-100 transition-colors"
                >
                  Browse Profiles
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-200">
            {filteredConversations.map((conv) => (
              <Link
                key={conv.id}
                to={`/messages/${conv.otherUser.id}`}
                state={{ returnUrl: '/messages', from: 'messages' }}
                className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
              >
                {/* Profile Image */}
                <div className="relative flex-shrink-0">
                  <img
                    src={
                      conv.otherUser.image ||
                      `https://ui-avatars.com/api/?name=${conv.otherUser.firstName}+${conv.otherUser.lastName}&size=100&background=random`
                    }
                    alt={`${conv.otherUser.firstName} ${conv.otherUser.lastName}`}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  {conv.unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-mint-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                      {conv.unreadCount}
                    </span>
                  )}
                </div>

                {/* Conversation Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {conv.otherUser.firstName} {conv.otherUser.lastName}
                      {conv.otherUser.isVerified && (
                        <span className="ml-2 text-green-600" title="Verified">
                          ✓
                        </span>
                      )}
                    </h3>
                    <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                      {formatTime(conv.lastMessageTime)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {conv.unreadCount > 0 && (
                      <Circle className="w-2 h-2 text-navy-500 fill-current flex-shrink-0" />
                    )}
                    <p
                      className={`text-sm truncate ${
                        conv.unreadCount > 0
                          ? 'text-gray-900 font-semibold'
                          : 'text-gray-600'
                      }`}
                    >
                      {conv.lastMessage || 'Start a conversation...'}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Help Text */}
        {conversations.length > 0 && (
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>
              You can only message users who have accepted your interest or whose interest
              you've accepted.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPageNew;
